import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Binary-searches the largest whole-word prefix of `fullText` for which
// `fits(candidate)` returns true. Pure/DOM-free so the trimming LOGIC (the
// ellipsis lands after a complete word, never mid-word) is unit-testable
// without a real browser layout engine.
export const trimToWordBoundary = (fullText, fits) => {
  if (!fullText || fits(fullText)) return fullText;
  const words = fullText.split(" ");
  let lo = 0;
  let hi = words.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = `${words.slice(0, mid).join(" ")}…`;
    if (fits(candidate)) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  const shown = lo === 0 ? words[0] : words.slice(0, lo).join(" ");
  return `${shown}…`;
};

// bell-panel-390-secondary-line-truncation (measured live on web-stg, bundle
// main.10820ec9.js): -webkit-line-clamp inserts its ellipsis by CHARACTER
// count, not by word boundary -- verified with a live side-by-side render,
// the SAME mid-word cut ("...Fitzgeraldingto...") happened identically with
// word-break: normal / break-word and overflow-wrap: normal / break-word /
// anywhere, so no CSS property controls where the clamp cuts. Trim to the
// last WHOLE word that fits the box ourselves and append our own ellipsis,
// so the native clamp (left in place -- it's still the guard against a
// single unbreakable long token overflowing, see
// NotificationItem.textbounds.test.jsx) never has to cut mid-word for
// ordinary text. Same scrollHeight > clientHeight overflow signal
// useIsTextTruncated already uses for this same box; jsdom reports both as 0
// (no real layout), so this hook is an inert passthrough under jsdom -- real
// measurement only happens in a real browser, same caveat the sibling hook
// documents.
const useWordSafeLineClamp = (fullText) => {
  const ref = useRef(null);
  const [displayText, setDisplayText] = useState(fullText);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) {
      setDisplayText(fullText);
      return;
    }
    const fits = (candidate) => {
      el.textContent = candidate;
      return el.scrollHeight <= el.clientHeight + 1;
    };
    const result = trimToWordBoundary(fullText, fits);
    // `fits` leaves `el.textContent` on whatever candidate it last tried (not
    // necessarily `result` -- binary search's last probe is often a rejected
    // one). Restore the DOM directly instead of relying on the setState below
    // to do it: if `result` happens to equal the PREVIOUS `displayText` (e.g.
    // a ResizeObserver re-fire computing the same answer), React bails out of
    // re-rendering (Object.is same-value check), which would leave the stale
    // rejected candidate visibly stuck in the DOM.
    el.textContent = result;
    setDisplayText(result);
  }, [fullText]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return [ref, displayText];
};

export default useWordSafeLineClamp;
